import { TApiResponse } from "@/types/api";
import { handleAsync } from "@/utils";
import { ApiService, TApiServiceError, TApiServiceOptions } from "../apiService";
import { withAuthHooks } from "../auth/auth.ky.hooks";
import {
  TLegalAcceptRequest,
  TLegalAcknowledgement,
  TLegalDocument,
  TLegalDocumentCreate,
  TLegalDocumentSummary,
  TLegalDocumentType,
  TLegalDocumentUpdate,
  TLegalAcknowledgementList,
  TLegalStatus,
} from "./legal.types";

const publicApi = new ApiService();
const authedApi = new ApiService({ hooks: { ...withAuthHooks } });

class LegalService {
  private rootEndpoint = "legal" as const;

  getActiveDocument(documentType: TLegalDocumentType, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TLegalDocument>>(() =>
      publicApi.get<TApiResponse<TLegalDocument>>(`${this.rootEndpoint}/documents/${documentType}`, options)
    );
  }

  getDocumentVersion(documentType: TLegalDocumentType, version: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TLegalDocument>>(() =>
      publicApi.get<TApiResponse<TLegalDocument>>(
        `${this.rootEndpoint}/documents/${documentType}/versions/${encodeURIComponent(version)}`,
        options
      )
    );
  }

  getStatus(options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TLegalStatus>>(() =>
      authedApi.get<TApiResponse<TLegalStatus>>(`${this.rootEndpoint}/status`, options)
    );
  }

  accept(payload: TLegalAcceptRequest, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TLegalAcknowledgement[]>>(() =>
      authedApi.post<TApiResponse<TLegalAcknowledgement[]>>(`${this.rootEndpoint}/accept`, payload, options)
    );
  }

  listDocuments(documentType?: TLegalDocumentType, options?: TApiServiceOptions) {
    const query = documentType ? `?document_type=${documentType}` : "";
    return handleAsync<TApiServiceError, TApiResponse<TLegalDocumentSummary[]>>(() =>
      authedApi.get<TApiResponse<TLegalDocumentSummary[]>>(`admin/${this.rootEndpoint}/documents${query}`, options)
    );
  }

  getDocumentById(documentId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TLegalDocument>>(() =>
      authedApi.get<TApiResponse<TLegalDocument>>(`admin/${this.rootEndpoint}/documents/${documentId}`, options)
    );
  }

  createDocument(payload: TLegalDocumentCreate, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TLegalDocument>>(() =>
      authedApi.post<TApiResponse<TLegalDocument>>(`admin/${this.rootEndpoint}/documents`, payload, options)
    );
  }

  updateDocument(documentId: string, payload: TLegalDocumentUpdate, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TLegalDocument>>(() =>
      authedApi.patch<TApiResponse<TLegalDocument>>(
        `admin/${this.rootEndpoint}/documents/${documentId}`,
        payload,
        options
      )
    );
  }

  activateDocument(documentId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TLegalDocument>>(() =>
      authedApi.post<TApiResponse<TLegalDocument>>(
        `admin/${this.rootEndpoint}/documents/${documentId}/activate`,
        undefined,
        options
      )
    );
  }

  listAcknowledgements(
    params?: { user_id?: string; document_type?: TLegalDocumentType; page?: number; page_size?: number },
    options?: TApiServiceOptions
  ) {
    const queryParams = new URLSearchParams();
    if (params?.user_id) queryParams.append("user_id", params.user_id);
    if (params?.document_type) queryParams.append("document_type", params.document_type);
    if (params?.page) queryParams.append("page", String(params.page));
    if (params?.page_size) queryParams.append("page_size", String(params.page_size));
    const query = queryParams.toString();
    const endpoint = query
      ? `admin/${this.rootEndpoint}/acknowledgements?${query}`
      : `admin/${this.rootEndpoint}/acknowledgements`;

    return handleAsync<TApiServiceError, TApiResponse<TLegalAcknowledgementList>>(() =>
      authedApi.get<TApiResponse<TLegalAcknowledgementList>>(endpoint, options)
    );
  }
}

export const legalService = new LegalService();
